import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import { createTemplate } from '@/api/messages'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'

/**
 * Keep the message being written as a template for next time — named here, then it's in "load a
 * template" for everyone in the agency (and in Settings → Templates), tokens and all.
 */
export default function SaveAsTemplateModal({
  channel,
  subject,
  body,
  onClose,
  onSaved,
}: {
  channel: 'email' | 'sms'
  subject: string
  body: string
  onClose: () => void
  onSaved: (name: string) => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState(subject.trim())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    setError(null)
    try {
      await createTemplate({ channel, name: name.trim(), subject: channel === 'email' ? subject : null, body })
      qc.invalidateQueries({ queryKey: ['templates'] })
      onSaved(name.trim())
    } catch (err) {
      setError(apiErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <Modal title="Save as template" onClose={onClose} size="sm">
      <form onSubmit={save} className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-500 mb-1">Template name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Appraisal follow-up"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
        </label>
        <p className="text-xs text-gray-500">
          Everyone in the agency will find it under “load a template”. Names and other fields stay as placeholders and are
          filled in for each person.
        </p>
        {error && <p className="text-danger text-xs">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={modalButton.ghost}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !name.trim()} className={modalButton.primary}>
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
